import { useState, useEffect } from 'react';
import { api, SubscriptionInfo, SubscriptionTier } from '../../lib/api';

export function SubscriptionCard() {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.getCurrentSubscription(), api.getSubscriptionTiers()])
      .then(([sub, tierResult]) => {
        setSubscription(sub);
        setTiers(tierResult.tiers);
      })
      .catch(() => setError('Failed to load subscription info'))
      .finally(() => setLoading(false));
  }, []);

  const handleUpgrade = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await api.subscribeToPremium();
      window.location.href = result.sessionUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start upgrade');
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await api.cancelSubscription();
      setSubscription((prev) =>
        prev ? { ...prev, cancelAtPeriodEnd: result.cancelAtPeriodEnd, status: 'active' } : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await api.reactivateSubscription();
      setSubscription((prev) =>
        prev ? { ...prev, cancelAtPeriodEnd: result.cancelAtPeriodEnd } : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reactivate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const result = await api.getSubscriptionPortalUrl();
      window.location.href = result.url;
    } catch {
      setError('Failed to open billing portal');
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-muted rounded w-32 mb-4"></div>
          <div className="h-20 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!subscription) return null;

  const premiumTier = tiers.find((t) => t.tier === 'premium');
  const isPremium = subscription.tier === 'premium';

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Your Plan</h2>
          <div className="flex items-center gap-2 mt-1">
            <span
              className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                isPremium ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}
            >
              {subscription.tierName}
            </span>
            {subscription.cancelAtPeriodEnd && (
              <span className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                Cancels{' '}
                {subscription.currentPeriodEnd
                  ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                  : 'at period end'}
              </span>
            )}
          </div>
        </div>
        {isPremium && (
          <button
            onClick={handleManageBilling}
            className="text-sm text-primary hover:text-primary/80"
          >
            Manage Billing
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          {error}
        </div>
      )}

      {isPremium ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Monthly Credits</span>
              <p className="font-medium text-foreground">
                {subscription.monthlyCreditAllocation.toLocaleString()}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Storage</span>
              <p className="font-medium text-foreground">{subscription.storageLimitGb} GB</p>
            </div>
            {subscription.currentPeriodEnd && (
              <div>
                <span className="text-muted-foreground">Next Billing</span>
                <p className="font-medium text-foreground">
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {subscription.cancelAtPeriodEnd ? (
            <button
              onClick={handleReactivate}
              disabled={actionLoading}
              className="w-full px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {actionLoading ? 'Processing...' : 'Reactivate Subscription'}
            </button>
          ) : (
            <button
              onClick={handleCancel}
              disabled={actionLoading}
              className="w-full px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50"
            >
              {actionLoading ? 'Processing...' : 'Cancel Subscription'}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Plan comparison */}
          {premiumTier && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">Upgrade to Premium</h3>
                <span className="text-lg font-bold text-primary">{premiumTier.displayPrice}</span>
              </div>
              <ul className="space-y-1.5 text-sm text-muted-foreground mb-4">
                {premiumTier.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <svg
                      className="w-4 h-4 text-primary flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={handleUpgrade}
                disabled={actionLoading}
                className="w-full px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Subscribe to Premium'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
